import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProjectWizardLayout } from '../components/Wizard/ProjectWizardLayout';
import { RequiredDocumentsUploader } from '../components/Wizard/RequiredDocumentsUploader';
import { useWizardStore } from '../store/wizardStore';
import wizardSchema from '../data/wizardSchema.json';

export const ProjectWizardDocumentsPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    currentDraft,
    isLoading,
    error,
    loadDraft,
    uploadDocument,
    deleteDocument
  } = useWizardStore();

  const totalSteps = wizardSchema.steps.length + 1; // +1 for documents step
  const requiredDocuments = wizardSchema.requiredDocuments;

  const allDocumentsUploaded = requiredDocuments.every(reqDoc =>
    currentDraft?.documents.some(doc => {
      // Check against document_subtype (new system) or fallback to type (old system)
      const docSubtype = doc.document_subtype || doc.type || doc.document_type;
      const requiredType = reqDoc.type.toUpperCase();
      console.log('Checking document:', { docSubtype, requiredType }, 'doc:', doc);
      return docSubtype === requiredType;
    })
  );

  // Debug logging
  console.log('Documents page - Current draft:', currentDraft);
  console.log('Documents page - All documents uploaded:', allDocumentsUploaded);
  console.log('Documents page - Required documents:', requiredDocuments);
  console.log('Documents page - Current documents:', currentDraft?.documents);

  // Load draft if not already loaded
  useEffect(() => {
    const initializeDraft = async () => {
      const savedDraftId = localStorage.getItem('currentDraftId');
      if (savedDraftId && !currentDraft) {
        try {
          await loadDraft(savedDraftId);
        } catch (error) {
          console.error('Failed to load draft:', error);
          navigate('/projects/new');
        }
      } else if (!savedDraftId) {
        navigate('/projects/new');
      }
    };

    initializeDraft();
  }, [currentDraft, loadDraft, navigate]);

  const handleUpload = async (type: string, file: File) => {
    if (!currentDraft) return;
    await uploadDocument(currentDraft.id, type, file);
  };

  const handleDelete = async (documentId: string) => {
    if (!currentDraft) return;
    await deleteDocument(currentDraft.id, documentId);
  };

  const handleNext = () => {
    navigate('/projects/new/review');
  };

  const handlePrevious = () => {
    navigate(`/projects/new/step/${wizardSchema.steps.length}`);
  };

  console.log('All documents uploaded check result:', allDocumentsUploaded);

  if (!currentDraft) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <ProjectWizardLayout
      currentStep={wizardSchema.steps.length + 1}
      totalSteps={wizardSchema.steps.length + 2}
      onPrevious={handlePrevious}
      onNext={handleNext}
      canGoNext={allDocumentsUploaded}
      canGoPrevious={true}
      isLoading={isLoading}
      title="Documents requis"
    >
      <RequiredDocumentsUploader
        requiredDocuments={requiredDocuments}
        uploadedDocuments={currentDraft.documents}
        onUpload={handleUpload}
        onDelete={handleDelete}
        isLoading={isLoading}
      />
      
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg mx-8 mb-4">
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      )}
    </ProjectWizardLayout>
  );
};